import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { CloudUpload, Loader2, CloudOff } from 'lucide-react';
import { sessionsAPI } from '../services/api';
import {
  isOnline,
  getPendingSyncCount,
  getPendingSessions,
  removePendingSession,
  clearOfflineGPSPoints,
} from '../services/offlineStorage';

const RETRY_MS = 60000; // auto-retry failed uploads every 60s

// Compact, non-blocking sync pill shown whenever offline-recorded sessions
// are waiting to upload. Auto-syncs when online, auto-retries on failure,
// tap to sync immediately.
export const OfflineSyncBanner = () => {
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [retryPending, setRetryPending] = useState(false);
  const syncingRef = useRef(false);
  const retryAtRef = useRef(0);

  const trySync = useCallback(async (manual = false) => {
    if (syncingRef.current || !isOnline()) return;
    const snapshot = getPendingSessions();
    if (!snapshot.length) { setPending(0); return; }
    if (!manual && Date.now() < retryAtRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    let synced = 0;
    let failed = 0;
    for (let i = snapshot.length - 1; i >= 0; i--) {
      const s = snapshot[i];
      try {
        await sessionsAPI.createSession({
          job_id: s.job_id,
          delivery_type: s.delivery_type,
          deliveries_count: s.deliveries_count,
          distance: s.distance,
          duration: s.duration,
          route_path: s.route_path,
          is_partial: s.is_partial,
          delivered_house_ids: s.delivered_house_ids,
          offline_data: {
            queuedAt: s.queuedAt,
            offlineGPSPoints: s.offlineGPSPoints || [],
          },
        });
        removePendingSession(i);
        synced++;
      } catch (e) {
        failed++;
      }
    }
    const left = getPendingSessions().length;
    if (synced > 0 && left === 0) {
      clearOfflineGPSPoints();
      toast.success(`Synced ${synced} offline session${synced > 1 ? 's' : ''}`, { duration: 3000 });
    }
    if (failed > 0) {
      retryAtRef.current = Date.now() + RETRY_MS;
      setRetryPending(true);
    } else {
      retryAtRef.current = 0;
      setRetryPending(false);
    }
    setPending(left);
    syncingRef.current = false;
    setSyncing(false);
  }, []);

  useEffect(() => {
    const tick = () => {
      setPending(getPendingSyncCount().sessions);
      trySync(false);
    };
    tick();
    // Delay after reconnect so TrackPage's own auto-sync (if mounted) goes first
    const onOnline = () => setTimeout(tick, 6000);
    window.addEventListener('online', onOnline);
    const iv = setInterval(tick, 15000);
    return () => {
      window.removeEventListener('online', onOnline);
      clearInterval(iv);
    };
  }, [trySync]);

  if (pending <= 0) return null;

  const label = syncing
    ? `Syncing ${pending} offline session${pending > 1 ? 's' : ''}…`
    : !isOnline()
      ? `${pending} session${pending > 1 ? 's' : ''} saved offline — will sync when online`
      : retryPending
        ? 'Upload failed — retrying automatically (tap to retry now)'
        : `${pending} offline session${pending > 1 ? 's' : ''} pending — tap to sync`;

  return (
    <button
      onClick={() => trySync(true)}
      className={`fixed top-2 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-xs font-semibold text-white max-w-[92vw] ${retryPending ? 'bg-amber-600/95' : 'bg-blue-600/95'}`}
      data-testid="offline-sync-banner"
    >
      {syncing
        ? <Loader2 className="w-4 h-4 animate-spin shrink-0" />
        : isOnline()
          ? <CloudUpload className="w-4 h-4 shrink-0" />
          : <CloudOff className="w-4 h-4 shrink-0" />}
      <span className="truncate" data-testid="offline-sync-banner-label">{label}</span>
    </button>
  );
};

export default OfflineSyncBanner;
